/**
 * Server-rack floor packing for rectangular or polygonal rooms.
 * Units are feet throughout.
 */

export function polygonArea(points) {
  if (!points || points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

export function polygonBounds(points) {
  if (!points?.length) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

export function rectanglePolygon(width, depth) {
  const w = Math.max(0, Number(width) || 0);
  const d = Math.max(0, Number(depth) || 0);
  return [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: d },
    { x: 0, y: d },
  ];
}

/** Ray-cast point-in-polygon. Boundary counts as inside. */
export function pointInPolygon(point, polygon) {
  if (!polygon || polygon.length < 3) return false;
  const { x, y } = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const onSeg =
      Math.abs((xj - xi) * (y - yi) - (x - xi) * (yj - yi)) < 1e-9 &&
      x >= Math.min(xi, xj) - 1e-9 &&
      x <= Math.max(xi, xj) + 1e-9 &&
      y >= Math.min(yi, yj) - 1e-9 &&
      y <= Math.max(yi, yj) + 1e-9;
    if (onSeg) return true;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 0) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function distPointToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) {
    const ex = p.x - a.x;
    const ey = p.y - a.y;
    return Math.hypot(ex, ey);
  }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function minDistanceToPolygonBoundary(point, polygon) {
  if (!polygon || polygon.length < 2) return Infinity;
  let min = Infinity;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    min = Math.min(min, distPointToSegment(point, a, b));
  }
  return min;
}

function sampleRectPoints(x, y, w, h) {
  return [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
    { x: x + w / 2, y: y + h / 2 },
    { x: x + w / 2, y },
    { x: x + w / 2, y: y + h },
    { x, y: y + h / 2 },
    { x: x + w, y: y + h / 2 },
  ];
}

/** Rack fits if its footprint is inside and clearance padding stays inside. */
export function rackFitsInPolygon(rack, polygon, wallClearance) {
  const pad = Math.max(0, wallClearance || 0);
  const samples = sampleRectPoints(rack.x, rack.y, rack.w, rack.h);
  for (const p of samples) {
    if (!pointInPolygon(p, polygon)) return false;
  }
  if (pad <= 0) return true;
  const expanded = sampleRectPoints(rack.x - pad, rack.y - pad, rack.w + 2 * pad, rack.h + 2 * pad);
  for (const p of expanded) {
    if (!pointInPolygon(p, polygon)) return false;
  }
  return true;
}

export function rectsOverlap(a, b, margin = 0) {
  const m = Math.max(0, margin || 0);
  return !(
    a.x + a.w + m <= b.x ||
    b.x + b.w + m <= a.x ||
    a.y + a.h + m <= b.y ||
    b.y + b.h + m <= a.y
  );
}

/** Reject racks that collide with pillars, vents, and other floor obstacles. */
export function rackClearsObstructions(rack, obstructions, clearance = 0) {
  if (!obstructions?.length) return true;
  for (const o of obstructions) {
    if (!o || !(o.w > 0) || !(o.h > 0)) continue;
    if (rectsOverlap(rack, o, clearance)) return false;
  }
  return true;
}

function buildRowYs({
  minY,
  maxY,
  rackDepth,
  wallClearance,
  coldAisle,
  hotAisle,
  pattern,
  originOffset,
}) {
  const rows = [];
  const cold = Math.max(0, coldAisle);
  const hot = Math.max(0, hotAisle);
  const depth = Math.max(0, rackDepth);
  if (depth <= 0) return rows;

  let y = minY + wallClearance + originOffset;

  if (pattern === "single") {
    while (y + depth + wallClearance <= maxY + 1e-9) {
      rows.push({ y, face: "front" });
      y += depth + cold;
    }
    return rows;
  }

  // Hot/cold pairs: [row][hot][row][cold]…
  while (y + depth + wallClearance <= maxY + 1e-9) {
    rows.push({ y, face: "front" });
    const yBack = y + depth + hot;
    if (yBack + depth + wallClearance <= maxY + 1e-9) {
      rows.push({ y: yBack, face: "back" });
      y = yBack + depth + cold;
    } else {
      break;
    }
  }
  return rows;
}

/** Merge overlapping [start,end] intervals; assume start <= end. */
function mergeIntervals(intervals) {
  if (!intervals.length) return [];
  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  const out = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i += 1) {
    const cur = sorted[i];
    const last = out[out.length - 1];
    if (cur.start <= last.end + 1e-9) {
      last.end = Math.max(last.end, cur.end);
    } else {
      out.push({ ...cur });
    }
  }
  return out;
}

/** Subtract blocked intervals from a free span. */
function subtractIntervals(freeStart, freeEnd, blocked) {
  let free = [{ start: freeStart, end: freeEnd }];
  const blocks = mergeIntervals(
    blocked.filter((b) => b.end > b.start + 1e-12).map((b) => ({
      start: b.start,
      end: b.end,
    }))
  );
  for (const block of blocks) {
    const next = [];
    for (const span of free) {
      if (block.end <= span.start + 1e-12 || block.start >= span.end - 1e-12) {
        next.push(span);
        continue;
      }
      if (block.start > span.start + 1e-12) {
        next.push({ start: span.start, end: Math.min(span.end, block.start) });
      }
      if (block.end < span.end - 1e-12) {
        next.push({ start: Math.max(span.start, block.end), end: span.end });
      }
    }
    free = next;
  }
  return free.filter((s) => s.end - s.start > 1e-9);
}

/**
 * Free spans for rack footprints along one axis, with overlapping obstacles carved out.
 * A rack footprint must lie fully inside [axisMin+wall, axisMax-wall].
 */
function freeSpansAlongAxis({
  axisMin,
  axisMax,
  wallClearance,
  rackAxisSize,
  blockedIntervals,
}) {
  const lo = axisMin + wallClearance;
  const hi = axisMax - wallClearance;
  if (hi - lo < rackAxisSize - 1e-9) return [];
  return subtractIntervals(lo, hi, blockedIntervals || []);
}

function packPositionsInSpans({ spans, size, gap, originOffset }) {
  const positions = [];
  let appliedOffset = false;
  for (const span of spans) {
    let x = span.start;
    if (!appliedOffset && originOffset > 0) {
      x += originOffset;
      appliedOffset = true;
    }
    while (x + size <= span.end + 1e-9) {
      positions.push(x);
      x += size + gap;
    }
  }
  return positions;
}

function blockedIntervalsForRowX(obstructions, rowY, rowDepth, clear) {
  const blocked = [];
  for (const o of obstructions || []) {
    const y0 = Number(o.y) || 0;
    const y1 = y0 + (Number(o.h) || 0);
    if (y1 + clear <= rowY || y0 - clear >= rowY + rowDepth) continue;
    const x0 = Number(o.x) || 0;
    const x1 = x0 + (Number(o.w) || 0);
    blocked.push({ start: x0 - clear, end: x1 + clear });
  }
  return blocked;
}

function blockedIntervalsForRowY(obstructions, rowX, rowDepth, clear) {
  const blocked = [];
  for (const o of obstructions || []) {
    const x0 = Number(o.x) || 0;
    const x1 = x0 + (Number(o.w) || 0);
    if (x1 + clear <= rowX || x0 - clear >= rowX + rowDepth) continue;
    const y0 = Number(o.y) || 0;
    const y1 = y0 + (Number(o.h) || 0);
    blocked.push({ start: y0 - clear, end: y1 + clear });
  }
  return blocked;
}

function packCandidate({
  polygon,
  rackWidth,
  rackDepth,
  wallClearance,
  coldAisle,
  hotAisle,
  sideGap,
  pattern,
  aisleAlong, // 'x' => aisles run horizontally (rows stacked in Y)
  offsetAlongAisle,
  offsetAcrossAisle,
  obstructions,
  obstructionClearance,
}) {
  const bounds = polygonBounds(polygon);
  const racks = [];
  const obs = Array.isArray(obstructions) ? obstructions : [];
  const obsClear = Math.max(0, obstructionClearance || 0);
  const gap = Math.max(0, sideGap);

  const accept = (rack) =>
    rackFitsInPolygon(rack, polygon, wallClearance) &&
    rackClearsObstructions(rack, obs, obsClear);

  if (aisleAlong === "x") {
    const rows = buildRowYs({
      minY: bounds.minY,
      maxY: bounds.maxY,
      rackDepth,
      wallClearance,
      coldAisle,
      hotAisle,
      pattern,
      originOffset: offsetAcrossAisle,
    });
    for (let r = 0; r < rows.length; r += 1) {
      const y = rows[r].y;
      const spans = freeSpansAlongAxis({
        axisMin: bounds.minX,
        axisMax: bounds.maxX,
        wallClearance,
        rackAxisSize: rackWidth,
        blockedIntervals: blockedIntervalsForRowX(obs, y, rackDepth, obsClear),
      });
      const positions = packPositionsInSpans({
        spans,
        size: rackWidth,
        gap,
        originOffset: offsetAlongAisle,
      });
      let col = 0;
      for (const pos of positions) {
        const rack = {
          x: pos,
          y,
          w: rackWidth,
          h: rackDepth,
          row: r,
          col: col++,
          face: rows[r].face,
          aisleAlong,
        };
        if (accept(rack)) racks.push(rack);
      }
    }
  } else {
    const rows = buildRowYs({
      minY: bounds.minX,
      maxY: bounds.maxX,
      rackDepth,
      wallClearance,
      coldAisle,
      hotAisle,
      pattern,
      originOffset: offsetAcrossAisle,
    });
    for (let r = 0; r < rows.length; r += 1) {
      const x = rows[r].y;
      const spans = freeSpansAlongAxis({
        axisMin: bounds.minY,
        axisMax: bounds.maxY,
        wallClearance,
        rackAxisSize: rackWidth,
        blockedIntervals: blockedIntervalsForRowY(obs, x, rackDepth, obsClear),
      });
      const positions = packPositionsInSpans({
        spans,
        size: rackWidth,
        gap,
        originOffset: offsetAlongAisle,
      });
      let col = 0;
      for (const pos of positions) {
        const rack = {
          x,
          y: pos,
          w: rackDepth,
          h: rackWidth,
          row: r,
          col: col++,
          face: rows[r].face,
          aisleAlong,
        };
        if (accept(rack)) racks.push(rack);
      }
    }
  }

  // Number only accepted racks so labels match the metric count (1..N).
  return racks.map((rack, i) => ({ ...rack, id: i + 1 }));
}

function offsetSteps(span, step, maxSteps = 12) {
  if (span <= 0 || step <= 0) return [0];
  const out = [];
  const count = Math.min(maxSteps, Math.floor(span / step) + 1);
  for (let i = 0; i < count; i += 1) out.push(i * step);
  return out;
}

/**
 * Side-gap / vertical cable manager rectangles between neighboring racks in a row.
 * aisleAlong 'x' => rows stack in Y (neighbors differ in X); 'y' => neighbors differ in Y.
 */
export function deriveSideGapRects(racks, aisleAlong) {
  if (!racks?.length) return [];
  const byRow = new Map();
  for (const r of racks) {
    const key = r.row;
    if (!byRow.has(key)) byRow.set(key, []);
    byRow.get(key).push(r);
  }
  const gaps = [];
  let id = 1;
  for (const rowRacks of byRow.values()) {
    rowRacks.sort((a, b) => (aisleAlong === "x" ? a.x - b.x : a.y - b.y));
    for (let i = 0; i < rowRacks.length - 1; i += 1) {
      const a = rowRacks[i];
      const b = rowRacks[i + 1];
      if (aisleAlong === "x") {
        const w = b.x - (a.x + a.w);
        if (w > 0.01) {
          gaps.push({ id: id++, x: a.x + a.w, y: Math.min(a.y, b.y), w, h: Math.min(a.h, b.h) });
        }
      } else {
        const h = b.y - (a.y + a.h);
        if (h > 0.01) {
          gaps.push({ id: id++, x: Math.min(a.x, b.x), y: a.y + a.h, w: Math.min(a.w, b.w), h });
        }
      }
    }
  }
  return gaps;
}

/**
 * Find max-rack layout across orientations, patterns, and small grid shifts.
 *
 * Default clearances follow common fiber-room practice aligned with TIA-942 aisle
 * guidance (4 ft preferred cold / 3 ft preferred hot). TIA-942 does not mandate a
 * side-to-side cabinet gap; fiber rows typically reserve ~6 in for vertical cable
 * managers between racks (DEFAULT via callers).
 */
export function optimizeRackLayout({
  polygon,
  rackWidth,
  rackDepth,
  wallClearance = 3,
  coldAisle = 4,
  hotAisle = 3,
  sideGap = 0.5,
  pattern = "hotCold", // 'hotCold' | 'single' | 'auto'
  orientation = "auto", // 'x' | 'y' | 'auto'  (aisleAlong)
  obstructions = [],
  obstructionClearance = 0,
}) {
  const poly = (polygon || []).map((p) => ({ x: Number(p.x), y: Number(p.y) }));
  if (poly.length < 3) {
    return {
      racks: [],
      sideGaps: [],
      count: 0,
      pattern: pattern === "auto" ? "hotCold" : pattern,
      aisleAlong: "x",
      roomArea: 0,
      rackArea: 0,
      utilization: 0,
      sideGap: Math.max(0, Number(sideGap) || 0),
    };
  }

  const rw = Math.max(0, Number(rackWidth) || 0);
  const rd = Math.max(0, Number(rackDepth) || 0);
  const wc = Math.max(0, Number(wallClearance) || 0);
  const cold = Math.max(0, Number(coldAisle) || 0);
  const hot = Math.max(0, Number(hotAisle) || 0);
  const gap = Math.max(0, Number(sideGap) || 0);

  const patterns = pattern === "auto" ? ["hotCold", "single"] : [pattern];
  const aisles =
    orientation === "auto" ? ["x", "y"] : [orientation === "y" ? "y" : "x"];

  const bounds = polygonBounds(poly);
  const step = Math.max(0.25, Math.min(rw, rd, 1) / 2);
  const acrossOffsets = offsetSteps(Math.min(bounds.height, bounds.width) * 0.35, step, 10);
  const alongOffsets = offsetSteps(Math.min(bounds.width, bounds.height) * 0.35, step, 10);

  let best = null;

  for (const pat of patterns) {
    for (const aisleAlong of aisles) {
      for (const offsetAcrossAisle of acrossOffsets) {
        for (const offsetAlongAisle of alongOffsets) {
          const racks = packCandidate({
            polygon: poly,
            rackWidth: rw,
            rackDepth: rd,
            wallClearance: wc,
            coldAisle: cold,
            hotAisle: hot,
            sideGap: gap,
            pattern: pat,
            aisleAlong,
            offsetAlongAisle,
            offsetAcrossAisle,
            obstructions,
            obstructionClearance,
          });
          const score = racks.length;
          if (
            !best ||
            score > best.count ||
            (score === best.count && pat === "hotCold" && best.pattern !== "hotCold")
          ) {
            best = {
              racks,
              count: score,
              pattern: pat,
              aisleAlong,
              offsetAlongAisle,
              offsetAcrossAisle,
            };
          }
        }
      }
    }
  }

  const roomArea = polygonArea(poly);
  const racks = best?.racks || [];
  const aisleAlong = best?.aisleAlong || aisles[0];
  const rackArea = racks.reduce((s, r) => s + r.w * r.h, 0);
  const utilization = roomArea > 0 ? rackArea / roomArea : 0;
  const sideGaps = deriveSideGapRects(racks, aisleAlong);

  return {
    racks,
    sideGaps,
    count: best?.count || 0,
    pattern: best?.pattern || patterns[0],
    aisleAlong,
    roomArea,
    rackArea,
    utilization,
    sideGap: gap,
  };
}

export function formatFeet(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)} ft`;
}
